/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { BasicResponseBinderAccessToken } from '../models/BasicResponseBinderAccessToken';
import type { BasicResponseBinderGoogleAccessToken } from '../models/BasicResponseBinderGoogleAccessToken';
import type { BasicResponseBoolean } from '../models/BasicResponseBoolean';
import type { BasicResponseEmotionTermListResponse } from '../models/BasicResponseEmotionTermListResponse';
import type { BasicResponseFilterOptionsResponse } from '../models/BasicResponseFilterOptionsResponse';
import type { BasicResponseGetBinderMemberResponse } from '../models/BasicResponseGetBinderMemberResponse';
import type { BasicResponseListIssueTableKeywordItem } from '../models/BasicResponseListIssueTableKeywordItem';
import type { BasicResponseListNotificationPopup } from '../models/BasicResponseListNotificationPopup';
import type { BasicResponseListPaymentHistoryItem } from '../models/BasicResponseListPaymentHistoryItem';
import type { BasicResponseListPaymentMethodItem } from '../models/BasicResponseListPaymentMethodItem';
import type { BasicResponsePersonalSettings } from '../models/BasicResponsePersonalSettings';
import type { BasicResponseString } from '../models/BasicResponseString';
import type { BasicResponseStringValueObject } from '../models/BasicResponseStringValueObject';
import type { BasicResponseSubscriptionInfo } from '../models/BasicResponseSubscriptionInfo';
import type { BasicResponseVoid } from '../models/BasicResponseVoid';
import type { BinderSignInViaGoogleCommand } from '../models/BinderSignInViaGoogleCommand';
import type { BinderSignInViaKakaoCommand } from '../models/BinderSignInViaKakaoCommand';
import type { IssueBillingKeyRequest } from '../models/IssueBillingKeyRequest';
import type { ModifyEmailRequest } from '../models/ModifyEmailRequest';
import type { ModifyPhoneNumberRequest } from '../models/ModifyPhoneNumberRequest';
import type { SubscriptionPackage } from '../models/SubscriptionPackage';

import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';

export class TeambinderApiService {

    /**
     * 바인더 구독 정보 조회
     * 사용자가 현재 구독중인 플랜에 대한 정보를 조회합니다.
     *
     * @returns BasicResponseSubscriptionInfo OK
     * @throws ApiError
     */
    public static getLatestSubscription({
        q,
    }: {
        /**
         * 조회 조건을 지정합니다. 'latest' 값으로 설정해야 합니다.
         */
        q: string,
    }): CancelablePromise<BasicResponseSubscriptionInfo> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/binder-app/members/me/subscriptions',
            query: {
                'q': q,
            },
            errors: {
                400: `Bad Request`,
                403: `Forbidden`,
                404: `Not Found`,
                409: `Conflict`,
                500: `Internal Server Error`,
            },
        });
    }

    /**
     * 바인더 구독 플랜 변경
     * 현재 구독중인 플랜을 변경합니다.<br>
     * 무료 플랜 subscriptionPlan 값은 `FREE`, billingCycle 값은 `INFINITE` 로 설정해야 합니다.<br>
     * 유료 플랜으로 변경할 경우, subscriptionPlan 값은 `BASIC`, `PREMIUM`, `ENTERPRISE` 중 택 1, billingCycle 값은 월결제, 연결제를 의미하는 `MONTHLY`, `YEARLY` 로 설정해야 합니다.<br>
     * 무료 체험 플랜 subscriptionPlan 값은 `ENTERPRISE_TRIAL`, billingCycle 값은 `TRIAL` 로 설정해야 합니다.<br>
     * 그 외의 입력값은 모두 실패처리 됩니다.<br><br>
     *
     * @returns BasicResponseVoid OK
     * @throws ApiError
     */
    public static changeSubscription({
        requestBody,
    }: {
        requestBody: SubscriptionPackage,
    }): CancelablePromise<BasicResponseVoid> {
        return __request(OpenAPI, {
            method: 'PUT',
            url: '/binder-app/members/me/subscriptions',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                400: `Bad Request`,
                403: `Forbidden`,
                404: `Not Found`,
                409: `Conflict`,
                500: `Internal Server Error`,
            },
        });
    }

    /**
     * 사용자 설정 값 변경
     * setting-name (설정 이름)
     * * 카카오 알림 수신 여부: `notification.kakao.enabled`
     * * 카카오 알림 발송 주기: `notification.kakao.interval`<br>
     * `AS_NEEDED`, `ONCE_A_DAY`, `ONCE_A_WEEK` 값 중 하나로 입력받을 수 있습니다.
     * * AS_NEEDED (수시로)
     * * ONCE_A_DAY (하루 1회)
     * * ONCE_A_WEEK (일주일 1회)
     * * 이메일 수신 여부: `notification.email.enabled`
     * * 이메일 발송 주기: `notification.email.interval`<br>
     * `AS_NEEDED`, `ONCE_A_DAY`, `ONCE_A_WEEK` 값 중 하나로 입력받을 수 있습니다.
     * * AS_NEEDED (수시로)
     * * ONCE_A_DAY (하루 1회)
     * * ONCE_A_WEEK (일주일 1회)
     *
     * @returns BasicResponseVoid OK
     * @throws ApiError
     */
    public static modifyPersonalSettings({
        settingName,
        value,
    }: {
        /**
         * 설정 이름
         */
        settingName: string,
        /**
         * 설정 값
         */
        value: string,
    }): CancelablePromise<BasicResponseVoid> {
        return __request(OpenAPI, {
            method: 'PUT',
            url: '/binder-app/members/me/settings/{setting-name}',
            path: {
                'setting-name': settingName,
            },
            query: {
                'value': value,
            },
            errors: {
                400: `Bad Request`,
                403: `Forbidden`,
                404: `Not Found`,
                409: `Conflict`,
                500: `Internal Server Error`,
            },
        });
    }

    /**
     * 휴대폰 번호 변경
     * @returns BasicResponseVoid OK
     * @throws ApiError
     */
    public static modifyPhoneNumber({
        requestBody,
    }: {
        requestBody: ModifyPhoneNumberRequest,
    }): CancelablePromise<BasicResponseVoid> {
        return __request(OpenAPI, {
            method: 'PUT',
            url: '/binder-app/members/me/phone-number',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                400: `Bad Request`,
                403: `Forbidden`,
                404: `Not Found`,
                409: `Conflict`,
                500: `Internal Server Error`,
            },
        });
    }

    /**
     * email 주소 번호 변경
     * @returns BasicResponseVoid OK
     * @throws ApiError
     */
    public static modifyEmail({
        requestBody,
    }: {
        requestBody: ModifyEmailRequest,
    }): CancelablePromise<BasicResponseVoid> {
        return __request(OpenAPI, {
            method: 'PUT',
            url: '/binder-app/members/me/email',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                400: `Bad Request`,
                403: `Forbidden`,
                404: `Not Found`,
                409: `Conflict`,
                500: `Internal Server Error`,
            },
        });
    }

    /**
     * Toss payments 빌링키 등록
     * Toss payments UI 를 이용해서 본인인증 및 카드등록이 완료합니다.<br>
     * 이후 응답으로 받게 되는 `authKey`, `customerKey` 를 API 호출 시 전달해야 합니다.
     *
     * @returns BasicResponseVoid OK
     * @throws ApiError
     */
    public static success({
        requestBody,
    }: {
        requestBody: IssueBillingKeyRequest,
    }): CancelablePromise<BasicResponseVoid> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/binder-app/payments/billing-key',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                400: `Bad Request`,
                403: `Forbidden`,
                404: `Not Found`,
                409: `Conflict`,
                500: `Internal Server Error`,
            },
        });
    }

    /**
     * 팀바인더 회원 탈퇴
     * @returns BasicResponseVoid OK
     * @throws ApiError
     */
    public static withdrawMembership1(): CancelablePromise<BasicResponseVoid> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/binder-app/members/me/withdraw',
            errors: {
                400: `Bad Request`,
                403: `Forbidden`,
                404: `Not Found`,
                409: `Conflict`,
                500: `Internal Server Error`,
            },
        });
    }

    /**
     * [바인더] 카카오 로그인 Token 발급 API
     * [로컬: 카카오 인증하기](http://localhost:8080/auth/binder/kakao/authorize) 접속
     *
     * @returns BasicResponseBinderAccessToken OK
     * @throws ApiError
     */
    public static signInViaKakao1({
        requestBody,
    }: {
        requestBody: BinderSignInViaKakaoCommand,
    }): CancelablePromise<BasicResponseBinderAccessToken> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/auth/binder/kakao/token',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                400: `Bad Request`,
                403: `Forbidden`,
                404: `Not Found`,
                409: `Conflict`,
                500: `Internal Server Error`,
            },
        });
    }

    /**
     * [바인더] 카카오 로그인 Token 발급 API
     * @returns BasicResponseString OK
     * @throws ApiError
     */
    public static getKakaoAccessToken({
        requestBody,
    }: {
        requestBody: BinderSignInViaKakaoCommand,
    }): CancelablePromise<BasicResponseString> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/auth/binder/kakao/accessToken',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                400: `Bad Request`,
                403: `Forbidden`,
                404: `Not Found`,
                409: `Conflict`,
                500: `Internal Server Error`,
            },
        });
    }

    /**
     * [바인더] 구글 로그인 Token 발급 API
     * Google OAuth 2.0 인증 코드를 사용하여 JWT 토큰을 발급합니다.<br>
     * [로컬 테스트용 구글 인증하기](http://localhost:8080/auth/binder/google/authorize) 접속<br><br>
     * 구글 인증을 시작하는 페이지로 이동하는 URL<br>
     * **https://accounts.google.com/o/oauth2/v2/auth?client_id={CLIENT_ID}&redirect_uri={REDIRECT_URI}&response_type=code&scope=email%20profile**<br><br>
     * CLIENT_ID: `95074272679-rvee3rru0t1qq37bk0vspu4u8iv0b6jv.apps.googleusercontent.com` 임시 클라이언트ID (변경 예정)<br>
     * REDIRECT_URI: `https://desk.marketdoctor.ai/auth/binder/google/authorize/callback` 임시 redirect url (변경 가능)<br><br>
     * [운영환경 구글 인증 시작](https://accounts.google.com/o/oauth2/v2/auth?client_id=95074272679-rvee3rru0t1qq37bk0vspu4u8iv0b6jv.apps.googleusercontent.com&redirect_uri=https://desk.marketdoctor.ai/auth/binder/google/authorize/callback&response_type=code&scope=email%20profile)
     *
     * @returns BasicResponseBinderGoogleAccessToken OK
     * @throws ApiError
     */
    public static signInViaGoogle({
        requestBody,
    }: {
        requestBody: BinderSignInViaGoogleCommand,
    }): CancelablePromise<BasicResponseBinderGoogleAccessToken> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/auth/binder/google/token',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                400: `Bad Request`,
                403: `Forbidden`,
                404: `Not Found`,
                409: `Conflict`,
                500: `Internal Server Error`,
            },
        });
    }

    /**
     * [바인더] 구글 Access Token 발급 API
     * @returns BasicResponseString OK
     * @throws ApiError
     */
    public static getGoogleAccessToken({
        requestBody,
    }: {
        requestBody: BinderSignInViaGoogleCommand,
    }): CancelablePromise<BasicResponseString> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/auth/binder/google/accessToken',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                400: `Bad Request`,
                403: `Forbidden`,
                404: `Not Found`,
                409: `Conflict`,
                500: `Internal Server Error`,
            },
        });
    }

    /**
     * 사용자 정보 조회
     * @returns BasicResponseGetBinderMemberResponse OK
     * @throws ApiError
     */
    public static getBinderMember(): CancelablePromise<BasicResponseGetBinderMemberResponse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/binder/member',
            errors: {
                400: `Bad Request`,
                403: `Forbidden`,
                404: `Not Found`,
                409: `Conflict`,
                500: `Internal Server Error`,
            },
        });
    }

    /**
     * 결제수단 목록 조회
     * 사용자가 등록한 결제수단(카드) 목록을 조회합니다.
     *
     * @returns BasicResponseListPaymentMethodItem OK
     * @throws ApiError
     */
    public static findPaymentMethods(): CancelablePromise<BasicResponseListPaymentMethodItem> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/binder-app/payments/payment-methods',
            errors: {
                400: `Bad Request`,
                403: `Forbidden`,
                404: `Not Found`,
                409: `Conflict`,
                500: `Internal Server Error`,
            },
        });
    }

    /**
     * customerKey 발급
     * 사용자 정보와 1:1로 매핑된 유니크한 `customerKey` 를 발행하는 API 입니다.<br>
     * Toss payments UI 로 카드등록 절차를 수행할 때, 클라이언트에서 임의의 customerKey 를 만들지 않고,<br>
     * API 에서 제공하는 customerKey 를 사용해야 합니다.
     *
     * @returns BasicResponseStringValueObject OK
     * @throws ApiError
     */
    public static getCustomerKey(): CancelablePromise<BasicResponseStringValueObject> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/binder-app/payments/customer-key',
            errors: {
                400: `Bad Request`,
                403: `Forbidden`,
                404: `Not Found`,
                409: `Conflict`,
                500: `Internal Server Error`,
            },
        });
    }

    /**
     * 바인더 구독 내역
     * 사용자의 구독 내역 리스트를 조회합니다.
     *
     * @returns BasicResponseListPaymentHistoryItem OK
     * @throws ApiError
     */
    public static getPaymentHistory({
        page,
        pageSize,
    }: {
        page: number,
        pageSize: number,
    }): CancelablePromise<BasicResponseListPaymentHistoryItem> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/binder-app/members/me/subscriptions/payment-overviews',
            query: {
                'page': page,
                'pageSize': pageSize,
            },
            errors: {
                400: `Bad Request`,
                403: `Forbidden`,
                404: `Not Found`,
                409: `Conflict`,
                500: `Internal Server Error`,
            },
        });
    }

    /**
     * 사용자 정보 및 설정값 조회
     * `testRemainingDays` 파라미터의 값으로 1~3 의 값이 입력되면, API 응답으로 무료체험 종료임박 알림 팝업 데이터를 수신할 수 있습니다.<br>
     * 테스트 용도로 사용할 수 있습니다.
     *
     * @returns BasicResponsePersonalSettings OK
     * @throws ApiError
     */
    public static getPersonalSettings({
        testRemainingDays,
    }: {
        testRemainingDays?: number,
    }): CancelablePromise<BasicResponsePersonalSettings> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/binder-app/members/me/settings',
            query: {
                'testRemainingDays': testRemainingDays,
            },
            errors: {
                400: `Bad Request`,
                403: `Forbidden`,
                404: `Not Found`,
                409: `Conflict`,
                500: `Internal Server Error`,
            },
        });
    }

    /**
     * 알림 팝업 정보 조회
     * `testRemainingDays` 파라미터의 값으로 1~3 의 값이 입력되면, API 응답으로 무료체험 종료임박 알림 팝업 데이터를 수신할 수 있습니다.<br>
     * 테스트 용도로 사용할 수 있습니다.
     *
     * @returns BasicResponseListNotificationPopup OK
     * @throws ApiError
     */
    public static getNotificationPopups({
        testRemainingDays,
    }: {
        testRemainingDays?: number,
    }): CancelablePromise<BasicResponseListNotificationPopup> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/binder-app/members/me/notification-popup',
            query: {
                'testRemainingDays': testRemainingDays,
            },
            errors: {
                400: `Bad Request`,
                403: `Forbidden`,
                404: `Not Found`,
                409: `Conflict`,
                500: `Internal Server Error`,
            },
        });
    }

    /**
     * 감성 키워드 리스트 조회
     * @returns BasicResponseEmotionTermListResponse OK
     * @throws ApiError
     */
    public static getEmotionTerms({
        keywordId,
        from,
        to,
        emotionalType,
        sentiments,
        categories,
        tags,
        platforms,
        isAd,
        page = 1,
        size = 20,
    }: {
        /**
         * 키워드 ID
         */
        keywordId: string,
        /**
         * 시작 날짜
         */
        from?: string,
        /**
         * 종료 날짜
         */
        to?: string,
        /**
         * 감성 타입 (null이면 전체보기)
         */
        emotionalType?: 'POSITIVE' | 'NEGATIVE',
        /**
         * 영향등급 리스트 (복수 선택 가능, null이면 전체보기)
         */
        sentiments?: Array<'VERY_GOOD' | 'GOOD' | 'SLIGHTLY_GOOD' | 'SLIGHTLY_BAD' | 'BAD' | 'VERY_BAD'>,
        /**
         * 카테고리 리스트 (복수 선택 가능, null이면 전체보기)
         */
        categories?: Array<'PRODUCT' | 'SERVICE' | 'BRAND' | 'MARKETING' | 'CUSTOMER_SUPPORT' | 'DISTRIBUTION' | 'ETC'>,
        /**
         * 태그 리스트 (복수 선택 가능, null이면 전체보기)
         */
        tags?: Array<string>,
        /**
         * 플랫폼 리스트 (복수 선택 가능, null이면 전체보기)
         */
        platforms?: Array<'NAVER_NEWS' | 'NAVER_BLOG' | 'NAVER_CAFE' | 'YOUTUBE' | 'INSTAGRAM' | 'TWITTER' | 'DCINSIDE' | 'BOBAEDREAM' | 'THEQOO' | 'PPOMPPU'>,
        /**
         * 광고성 필터 (null=전체보기, true=광고성, false=비광고성)
         */
        isAd?: boolean,
        /**
         * 페이지 번호 (1부터 시작)
         */
        page?: number,
        /**
         * 페이지 크기
         */
        size?: number,
    }): CancelablePromise<BasicResponseEmotionTermListResponse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/binder-app/ai-realtime/keywords/{keyword-id}/emotion-terms',
            path: {
                'keyword-id': keywordId,
            },
            query: {
                'from': from,
                'to': to,
                'emotionalType': emotionalType,
                'sentiments': sentiments,
                'categories': categories,
                'tags': tags,
                'platforms': platforms,
                'isAd': isAd,
                'page': page,
                'size': size,
            },
            errors: {
                400: `Bad Request`,
                403: `Forbidden`,
                404: `Not Found`,
                409: `Conflict`,
                500: `Internal Server Error`,
            },
        });
    }

    /**
     * AI 실시간 감지 이슈 테이블 키워드 리스트 조회
     * @returns BasicResponseListIssueTableKeywordItem OK
     * @throws ApiError
     */
    public static getIssueTableKeywords(): CancelablePromise<BasicResponseListIssueTableKeywordItem> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/binder-app/ai-realtime/issue-table/keywords',
            errors: {
                400: `Bad Request`,
                403: `Forbidden`,
                404: `Not Found`,
                409: `Conflict`,
                500: `Internal Server Error`,
            },
        });
    }

    /**
     * 감성 키워드 필터 선택지 조회
     * @returns BasicResponseFilterOptionsResponse OK
     * @throws ApiError
     */
    public static getFilterOptions({
        selectedCategories,
    }: {
        /**
         * 선택된 카테고리 리스트 (태그 필터링용)
         */
        selectedCategories?: Array<'PRODUCT' | 'SERVICE' | 'BRAND' | 'MARKETING' | 'CUSTOMER_SUPPORT' | 'DISTRIBUTION' | 'ETC'>,
    }): CancelablePromise<BasicResponseFilterOptionsResponse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/binder-app/ai-realtime/emotion-terms/filter-options',
            query: {
                'selectedCategories': selectedCategories,
            },
            errors: {
                400: `Bad Request`,
                403: `Forbidden`,
                404: `Not Found`,
                409: `Conflict`,
                500: `Internal Server Error`,
            },
        });
    }

    /**
     * 결제수단 삭제
     * 사용자가 등록한 결제수단(카드)을 삭제합니다.
     *
     * @returns BasicResponseBoolean OK
     * @throws ApiError
     */
    public static deletePaymentMethod({
        paymentMethodId,
    }: {
        paymentMethodId: string,
    }): CancelablePromise<BasicResponseBoolean> {
        return __request(OpenAPI, {
            method: 'DELETE',
            url: '/binder-app/payments/payment-methods/{paymentMethodId}',
            path: {
                'paymentMethodId': paymentMethodId,
            },
            errors: {
                400: `Bad Request`,
                403: `Forbidden`,
                404: `Not Found`,
                409: `Conflict`,
                500: `Internal Server Error`,
            },
        });
    }

}
